import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { FaHandHoldingUsd, FaSpinner, FaTimes, FaUpload } from 'react-icons/fa';
import { API_URL } from '../config';

// ============================================
// RECEIVE PAYMENT  (one bank line, many invoices)
// ============================================
// A client often settles SEVERAL invoices with ONE transfer. This modal lists the client's
// OPEN invoices, pre-fills each with its full balance and allocates OLDEST FIRST; you can
// edit any line, then record it as ONE receipt (RCT-00001) that spans every invoice it
// touched. The backend caps each allocation at that invoice's outstanding balance, so it
// can never over-pay an invoice.
//
// Props: onDone (called after a successful save so the page can refresh)
// ============================================

const money = (n) => 'R ' + (Number(n) || 0).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-ZA', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');
const todayIso = () => new Date().toISOString().slice(0, 10);

export default function ReceivePaymentButton({ onDone, className }) {
  const [open, setOpen] = useState(false);
  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = useState('');
  const [invoices, setInvoices] = useState([]);
  const [amounts, setAmounts] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ payment_date: todayIso(), method: 'bank_transfer', reference: '', notes: '' });
  const [file, setFile] = useState(null);

  const auth = () => ({
    'Content-Type': 'application/json',
    Authorization: `Bearer ${localStorage.getItem('token') || ''}`
  });

  // Load the active client list the first time the modal opens.
  useEffect(() => {
    if (!open || clients.length > 0) return;
    (async () => {
      try {
        const r = await fetch(`${API_URL}/clients`, { headers: auth() });
        const d = await r.json();
        if (d.success) setClients((d.clients || []).filter((c) => c.status === 'active'));
      } catch { /* the picker just stays empty */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const reset = () => {
    setClientId('');
    setInvoices([]);
    setAmounts({});
    setForm({ payment_date: todayIso(), method: 'bank_transfer', reference: '', notes: '' });
    setFile(null);
  };

  const loadOpen = async (id) => {
    if (!id) { setInvoices([]); setAmounts({}); return; }
    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/invoices/open/${id}`, { headers: auth() });
      const d = await r.json();
      if (!d.success) throw new Error(d.error || 'Could not load the open invoices');
      const list = d.invoices || [];
      setInvoices(list);
      // Default to "they paid everything", oldest first - editable per invoice.
      const pre = {};
      list.forEach((inv) => { pre[inv.id] = inv.balance; });
      setAmounts(pre);
    } catch (e) {
      toast.error(e.message);
      setInvoices([]);
      setAmounts({});
    } finally {
      setLoading(false);
    }
  };

  const allocated = invoices.reduce((s, inv) => s + (Number(amounts[inv.id]) || 0), 0);
  const outstanding = invoices.reduce((s, inv) => s + (Number(inv.balance) || 0), 0);

  const setAmountFor = (id, value) => {
    const n = Math.max(Number(value) || 0, 0);
    setAmounts((prev) => ({ ...prev, [id]: n }));
  };

  // Spread a total across the invoices, oldest first.
  const allocateOldestFirst = (total) => {
    let left = Number(total) || 0;
    const next = {};
    for (const inv of invoices) {
      const take = Math.min(left, Number(inv.balance) || 0);
      next[inv.id] = Math.round(take * 100) / 100;
      left = Math.round((left - take) * 100) / 100;
    }
    setAmounts(next);
  };

  const save = async () => {
    if (!clientId) { toast.error('Pick a client first'); return; }
    const allocations = invoices
      .map((inv) => ({ invoice_id: inv.id, amount: Number(amounts[inv.id]) || 0 }))
      .filter((a) => a.amount > 0);
    if (allocations.length === 0) { toast.error('Allocate the payment to at least one invoice'); return; }

    setSaving(true);
    try {
      // Optional proof of payment.
      let pop_url = null;
      if (file) {
        const fd = new FormData();
        fd.append('image', file);
        const ur = await fetch(`${API_URL}/uploads`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${localStorage.getItem('token') || ''}` },
          body: fd
        });
        const ud = await ur.json();
        if (ud.success) pop_url = ud.url || ud.file_url || null;
      }

      const r = await fetch(`${API_URL}/invoices/receive-payment`, {
        method: 'POST',
        headers: auth(),
        body: JSON.stringify({
          client_id: clientId,
          payment_date: form.payment_date,
          method: form.method,
          reference: form.reference || null,
          notes: form.notes || null,
          pop_url,
          allocations
        })
      });
      const d = await r.json();
      if (!d.success) throw new Error(d.error || 'Could not record the payment');
      toast.success(d.message || 'Payment recorded');
      setOpen(false);
      reset();
      if (onDone) onDone();
    } catch (err) {
      toast.error(err.message || 'Could not record the payment');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className={className || 'flex items-center gap-2 px-4 py-2 bg-green text-white rounded-lg text-sm font-medium hover:bg-navy transition-colors'}
      >
        <FaHandHoldingUsd size={14} /> Receive payment
      </button>

      {open && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center">
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => { setOpen(false); reset(); }}></div>
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-3xl w-full mx-4 p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-start justify-between mb-1">
              <h2 className="text-xl font-bold text-navy">Receive payment</h2>
              <button onClick={() => { setOpen(false); reset(); }} className="text-gray-400 hover:text-navy"><FaTimes size={16} /></button>
            </div>
            <p className="text-xs text-gray-500 mb-4">
              One payment can settle several invoices. Allocate it below — anything already paid is excluded.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Client *</label>
                <select
                  value={clientId}
                  onChange={(e) => { setClientId(e.target.value); loadOpen(e.target.value); }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                >
                  <option value="">Select a client…</option>
                  {clients.map((c) => <option key={c.id} value={c.id}>{c.company_name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Date received *</label>
                <input type="date" value={form.payment_date} onChange={(e) => setForm({ ...form, payment_date: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Method</label>
                <select value={form.method} onChange={(e) => setForm({ ...form, method: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
                  <option value="bank_transfer">Bank transfer (EFT)</option>
                  <option value="cash">Cash</option>
                  <option value="card">Card</option>
                  <option value="cheque">Cheque</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Reference / bank ref</label>
                <input value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} placeholder="e.g. the bank reference" className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
              </div>
            </div>

            {/* Allocation across the client's open invoices */}
            {loading ? (
              <div className="py-10 text-center text-gray-400"><FaSpinner className="animate-spin inline" size={20} /></div>
            ) : clientId && invoices.length === 0 ? (
              <p className="text-sm text-gray-400 mb-4">This client has no open invoices — nothing to allocate.</p>
            ) : invoices.length > 0 ? (
              <div className="mb-4">
                <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                  <span className="text-sm font-medium text-navy">Apply to ({invoices.length} open)</span>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-500">Total received</span>
                    <input
                      type="number"
                      step="0.01"
                      placeholder={outstanding.toFixed(2)}
                      onChange={(e) => allocateOldestFirst(e.target.value)}
                      className="w-32 px-2 py-1 border border-gray-300 rounded-lg text-sm text-right"
                    />
                    <button
                      type="button"
                      onClick={() => allocateOldestFirst(outstanding)}
                      className="text-xs px-2 py-1 rounded-lg border border-gray-200 text-gray-600 hover:border-green hover:text-green"
                    >
                      All oldest-first
                    </button>
                  </div>
                </div>
                <div className="space-y-1">
                  {invoices.map((inv) => (
                    <div key={inv.id} className="flex items-center gap-3 bg-gray-50 rounded-lg px-3 py-2">
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-navy truncate">
                          {inv.invoice_number}{inv.proforma_number ? ` · ${inv.proforma_number}` : ''}
                        </p>
                        <p className="text-xs text-gray-400">
                          {fmtDate(inv.invoice_date)} · due {fmtDate(inv.due_date)} · balance {money(inv.balance)}
                        </p>
                      </div>
                      <input
                        type="number"
                        step="0.01"
                        value={amounts[inv.id] ?? ''}
                        onChange={(e) => setAmountFor(inv.id, e.target.value)}
                        className="w-32 px-2 py-1 border border-gray-300 rounded-lg text-sm text-right"
                      />
                    </div>
                  ))}
                </div>
                <div className="flex justify-between text-sm mt-2 px-1">
                  <span className="text-gray-500">Allocated</span>
                  <span className="font-semibold text-navy">{money(allocated)} of {money(outstanding)} outstanding</span>
                </div>
              </div>
            ) : null}

            {/* Proof of payment + notes */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-2">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Proof of payment (optional)</label>
                <label className="flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-300 text-sm cursor-pointer hover:bg-gray-50">
                  <FaUpload size={12} /> {file ? file.name : 'Choose a file…'}
                  <input type="file" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} />
                </label>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Notes</label>
                <input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-5">
              <button type="button" onClick={() => { setOpen(false); reset(); }} className="px-4 py-2 rounded-lg border border-gray-200 text-sm hover:bg-gray-50">Cancel</button>
              <button
                type="button"
                onClick={save}
                disabled={saving || !clientId || allocated <= 0}
                className="px-6 py-2 rounded-lg text-sm font-semibold text-white bg-green hover:bg-green-600 disabled:opacity-50"
              >
                {saving ? 'Recording…' : `Record receipt ${money(allocated)}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

